import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, EventEmitter, Input, Output } from '@angular/core';
import { Constants } from '../../constants';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { toggleTotalPremiumAndAdhesiveCalculator } from '../../utils/premium-and-adhesive-calculator';
import { RequestParamModel } from '@coremodels';
import { QuoteOrderSampleService, ProductsService } from '@coreapiservices';
import { AlertService, HelperService, SubscribersService } from '@coreservices';
import { CurrencyPipe, JsonPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QuoteService } from '../../apiservices/quote.service';
import { SliderComponent } from '../../../shared/components/slider/slider.component';
import { firstValueFrom } from 'rxjs';
import { RouterModule } from '@angular/router';
import { NgbDropdownButtonItem, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
    QuoteInvoiceExportModalComponent
} from "../../../shared/modal/quote-invoice-export-modal/quote-invoice-export-modal.component";
import {
    UpdateStorageFeeDeliveryModalComponent
} from "../../../shared/modal/update-storage-fee-delivery-modal/update-storage-fee-delivery-modal.component";

@Component({
    selector: 'app-order-summary',
    // Eager: Angular 22 defaults every component to OnPush, which would leave this view
    // unpainted after its subscribe callbacks (see app.component.ts for the full note).
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
    imports: [CurrencyPipe, TooltipModule, FormsModule, ReactiveFormsModule, SliderComponent, RouterModule],
    templateUrl: './order-summary.component.html',
    styleUrl: './order-summary.component.css'
})
export class OrderSummaryComponent {
    @Input() modelOrder: any;

    /**
     * True when the quote holds at least one ordinary product line.
     *
     * The two "apply to every item in the order" checkboxes next to Total Premium Adhesive / Sealer
     * drive the PER-PRODUCT consumable calculators. A consumables-only quote has no products for them
     * to act on, so ticking one did nothing to the money and only re-ran
     * toggleTotalPremiumAndAdhesiveCalculator() — which used to hide both rows outright. Hide the
     * controls instead of offering a no-op.
     */
    get hasProductLine(): boolean {
        return (this.modelOrder?.quote_product || [])
            .some((line: any) => !!line?.product_id || !line?.consumable_id);
    }
    @Input() isPreviewDetail: any;
    @Input() isQuote: any
    @Input() isQuoteAccepted: any
    @Input() isDisabledQuoteDetail: any
    @Input() isQuoteCancel: any
    @Input() isQuoteUnconverted: any
    @Input() isOfflineOrder: boolean = false
    @Input() isOrderCancel: any
    @Input() isSampleDetail: any;
    @Input() isOrderFromQuote: any;
    /** When true, disable the Storage Fees toggle. Export Invoice stays. */
    @Input() isOfflineQuote: boolean = false;
    @Output("getDetailEvent") getDetailEvent: EventEmitter<any> = new EventEmitter();
    constants = Constants
    sliderValue = 50;
    @Input() isHideSlider: boolean = false
    consumableCalculator: any
    totalCrates = computed(() => this.subscriberService?.totalCrates());

    @Output() isLoadingEvent = new EventEmitter<boolean>();

    storageFeeCheckboxState: 'all' | 'partial' | 'none' = 'none';

    constructor(
        private modalService: BsModalService,
        private modalService1: NgbModal,
        private changeDetection: ChangeDetectorRef,
        private orderService: QuoteOrderSampleService,
        private productService: ProductsService,
        private alertService: AlertService,
        private helperFunction: HelperService,
        private quoteService: QuoteService,
        private subscriberService: SubscribersService
    ) {
    }

    ngOnInit() {
        this.updateStorageFeeCheckboxState();
    }

    ngOnChanges() {
        this.updateStorageFeeCheckboxState();
    }

    updateStorageFeeCheckboxState() {
        if (!this.modelOrder?.is_split_delivery) return;

        const deliveries = this.getScheduleDeliveries();

        // Exclude delivered/despatched — they are always unchecked and non-toggleable
        const activeDeliveries = deliveries.filter((d: any) =>
            d.status_id !== Constants.STATUS_DELIVERD_ID &&
            d.status_id !== Constants.STATUS_DELIVERY_DESPATCHED_ID
        );

        if (!activeDeliveries.length) {
            this.storageFeeCheckboxState = 'none';
            return;
        }

        const allIncluded = activeDeliveries.every((d: any) => d.is_consider_for_storage_fees);
        const noneIncluded = activeDeliveries.every((d: any) => !d.is_consider_for_storage_fees);

        this.storageFeeCheckboxState = noneIncluded ? 'none' : allIncluded ? 'all' : 'partial';
    }

    getScheduleDeliveries(): any[] {
        const deliveries: any[] = [];
        this.modelOrder?.quote_address?.forEach((addr: any) => {
            addr.quote_schedule_delivery?.forEach((del: any) => {
                deliveries.push(del);
            });
        });
        return deliveries;
    }

    onSplitDeliveryStorageFeeCheckboxClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.modelOrder?.status?.short_name === this.constants.ORDER_DELIVERD_SHORT ||
            this.modelOrder?.status?.short_name === this.constants.ORDER_DELIVERY_DISPATCHED_SHORT) {
            return;
        }

        this.openStorageFeeDeliveryModal();
    }

    openStorageFeeDeliveryModal() {
        let modalRef: any = this.modalService1.open(UpdateStorageFeeDeliveryModalComponent, {
            centered: true,
            windowClass: 'donationModals quotes-model',
            backdrop: 'static',
        });
        modalRef.componentInstance.modelOrder = this.modelOrder;
        modalRef.closed.subscribe((result: any) => {
            if (result) {
                this.getDetailEvent.emit(false);
            }
        });
    }

    getFormattedDiscount(discount?: number | string | null): string {
        const parsedDiscount = Number(discount);
        if (isNaN(parsedDiscount)) {
            return '20%';
        }
        return parsedDiscount % 1 === 0
            ? `${parsedDiscount}%`
            : `${parsedDiscount.toFixed(2)}%`;
    }

    private showConfirmModal(title: string, message: string): Promise<boolean> {
        return new Promise((resolve) => {
            let modalRef: any = this.modalService.show(ConfirmModalComponent);
            modalRef.setClass('delete-favourite-modal');
            modalRef.content.title = title;
            modalRef.content.message = message;
            modalRef.content.onClose.subscribe((result: any) => {
                resolve(result);
            });
        });
    }

    async toggleAdhesiveAndPremiumCalculator(e: any, type: any = null) {
        if (!this.modelOrder?.quote_product?.length || this.modelOrder?.is_archive || this.modelOrder?.is_expired) return;

        let currentChecked = e.target.checked;

        // Determine if confirmation is needed (credit applied + toggle attempt)
        if (this.modelOrder.applied_credit > 0) {
            const isAdhesiveToggle = type === Constants.CONSUMABLE_CALCULATOR_TYPE_ADHESIVE;
            const isPremiumToggle = type === null;

            // Show confirm modal if toggling adhesive or premium sealer
            if (isAdhesiveToggle || isPremiumToggle) {
                e.preventDefault(); // Prevent auto-toggle
                const title = currentChecked
                    ? 'Confirm Remove Consumable Calculation'
                    : 'Confirm Add Consumable Calculation';
                const message = currentChecked
                    ? Constants.REMOVE_CONSUMABLE_CALCULATOR_AFTER_CREDIT_APPLIED.BODY_MESSAGE
                    : Constants.ADD_CONSUMABLE_CALCULATOR_AFTER_CREDIT_APPLIED.BODY_MESSAGE;

                const confirmed = await this.showConfirmModal(title, message);
                if (!confirmed) return;

                // Toggle manually based on confirmation
                e.target.checked = !currentChecked;
                currentChecked = e.target.checked; // update after manual toggle
            }
        }

        // ✅ START LOADER
        this.isLoadingEvent.emit(true);

        try {
            const isChecked = e.target.checked;

            // 3. Update is_selected flags (Synchronous)
            this.modelOrder.quote_product.forEach((product: any) => {
                product.quoteProductCalculators?.forEach((calculator: any) => {
                    const isAdhesive =
                        calculator.type === Constants.CONSUMABLE_CALCULATOR_TYPE_ADHESIVE &&
                        product.product.apply_glue_calculator === Constants.APPLY_GLUE_CALCULATOR_YES &&
                        type === Constants.CONSUMABLE_CALCULATOR_TYPE_ADHESIVE;

                    const isPremium =
                        (calculator.type === Constants.CONSUMABLE_CALCULATOR_TYPE_DEFENDER ||
                            calculator.type === Constants.CONSUMABLE_CALCULATOR_TYPE_GOLD) &&
                        type === null;

                    if (isAdhesive || isPremium) {
                        calculator.is_selected = isChecked;
                    }
                });
            });

            // 4. Local Calculations
            toggleTotalPremiumAndAdhesiveCalculator(this.modelOrder, this.constants);
            this.changeDetection.detectChanges();

            // 5. ASYNC PROCESSES
            // Save applied calculator updates (Handles multiple API calls via forkJoin)
            // Backend ALREADY recalculates quote_summary in QuoteProductAppliedCalculatorController
            // (actionCreate/actionDelete calls $freshQuote->updateQuotePrice() at lines 163 & 299)
            await this.updateQuoteProductAppliedCalculator();

            // ✅ FIX: Fetch fresh quote_summary values from backend
            // Backend already recalculated totals in the save/delete API above
            await this.refreshQuoteSummary();

            // Sync with Unleashed if it's an Order (now uses fresh values)
            if (this.modelOrder.type === Constants.ORDER_TYPE_ORDER_SHORT && !this.isOfflineOrder) {
                await this.syncProductWithUnleashed();
            }

            // 6. Reset applied credit and notify components
            this.modelOrder.applied_credit = 0;
            this.getDetailEvent.emit(false);

        } catch (err) {
            console.error('Failed to toggle adhesive/premium calculators', err);
        } finally {
            // ✅ HIDE LOADER (Guaranteed to execute)
            this.isLoadingEvent.emit(false);
            this.changeDetection.detectChanges();
        }
    }

    async toggleStorageFeesCalculator(e: any) {
        if (!this.modelOrder?.quote_product?.length || this.modelOrder?.is_archive || this.modelOrder?.is_expired) return;

        const isChecked = e.target.checked;

        // Show loader
        this.isLoadingEvent.emit(true);

        try {
            // Set the flag locally
            this.modelOrder.is_storage_fee_applicable = isChecked;

            // Call API with the is_storage_fee_applicable value
            await firstValueFrom(this.quoteService.updateStorageFeeApplicable(
                { is_storage_fee_applicable: isChecked },
                this.modelOrder.id
            ));

            // Notify components of the change
            this.getDetailEvent.emit(false);
        } catch (err) {
            console.error('Failed to update storage fees', err);
        } finally {
            // Hide loader
            this.isLoadingEvent.emit(false);
            this.changeDetection.detectChanges();
        }
    }

    /**
     * Check if order has reached warehouse (status in range 29-36)
     * Warehouse statuses: AAW, SD, DUNCONF, DOTT, DCONF, DCO, DD, DEL
     */
    checkIfOrderReachedAAW(): boolean {
        if (!this.modelOrder?.status_id) return false;

        const WAREHOUSE_STATUS_RANGE = [29, 30, 31, 32, 33, 34, 35, 36];
        return WAREHOUSE_STATUS_RANGE.includes(this.modelOrder.status_id);
    }

    syncProductWithUnleashed() {
        if (this.modelOrder.id > 0) {
            return new Promise((resolve, reject) => {
                this.quoteService.syncProductWithUnleashed(null, this.modelOrder.id).subscribe({
                    next: () => {
                        resolve(true);
                    },
                    error: (err) => {
                        console.error(`🚨 [OrderSummary] Sync API failed for quote ${this.modelOrder.id}`, err);
                        reject(false);
                    }
                });
            });
        }
        // Return resolved promise if no sync needed
        return Promise.resolve();
    }

    async updateQuoteProductAppliedCalculator(isDisplayFlashMessage: boolean = true): Promise<boolean> {
        try {
            // ✅ FIX: Execute operations SEQUENTIALLY to prevent database deadlocks
            // When DEFENDER/GOLD calculators are saved/deleted, the backend performs
            // price redistribution which updates ALL calculators of that type.
            // Parallel execution causes deadlocks when multiple operations try to
            // update the same calculator records simultaneously.
            for (const product of this.modelOrder.quote_product) {
                for (const calculator of product.quoteProductCalculators || []) {
                    if (calculator.is_selected === true) {
                        const form: any = {
                            quote_product_id: product.id,
                            type: calculator.type,
                            total_unit: calculator.total_unit,
                            total_price: calculator.total_price,
                        };

                        if (this.modelOrder.type === Constants.ORDER_TYPE_QUOTE_SHORT) {
                            form.applied_credit = 0;
                        }

                        try {
                            const res: any = await firstValueFrom(
                                this.orderService.saveQuoteProductAppliedCalculator(form)
                            );
                            calculator.id = res;
                            if (isDisplayFlashMessage) {
                                this.alertService.success(this.modelOrder.type === Constants.ORDER_TYPE_QUOTE_SHORT ? Constants.CONSUMABLE_CALCULATOR_CONSTANTS.ADDED_TO_QUOTE_SUCCESS_MESSAGE : Constants.CONSUMABLE_CALCULATOR_CONSTANTS.ADDED_TO_ORDER_SUCCESS_MESSAGE);
                            }
                        } catch (error) {
                            this.alertService.error(this.modelOrder.type === Constants.ORDER_TYPE_QUOTE_SHORT ? Constants.CONSUMABLE_CALCULATOR_CONSTANTS.ADDED_TO_QUOTE_ERROR_MESSAGE : Constants.CONSUMABLE_CALCULATOR_CONSTANTS.ADDED_TO_ORDER_ERROR_MESSAGE);
                        }
                    } else if (calculator.is_selected === false && calculator.id > 0) {
                        try {
                            await firstValueFrom(
                                this.orderService.deleteQuoteProductAppliedCalculator(calculator.id)
                            );
                            delete calculator.id;
                            if (isDisplayFlashMessage) {
                                this.alertService.success(this.modelOrder.type === Constants.ORDER_TYPE_QUOTE_SHORT ? Constants.CONSUMABLE_CALCULATOR_CONSTANTS.REMOVED_FROM_QUOTE_SUCCESS_MESSAGE : Constants.CONSUMABLE_CALCULATOR_CONSTANTS.REMOVED_FROM_ORDER_SUCCESS_MESSAGE);
                            }
                        } catch (error) {
                            this.alertService.error(this.modelOrder.type === Constants.ORDER_TYPE_QUOTE_SHORT ? Constants.CONSUMABLE_CALCULATOR_CONSTANTS.REMOVED_FROM_QUOTE_ERROR_MESSAGE : Constants.CONSUMABLE_CALCULATOR_CONSTANTS.REMOVED_FROM_ORDER_ERROR_MESSAGE);
                        }
                    }
                }
            }
            this.changeDetection.detectChanges();
            return true;
        } catch (error) {
            return false;
        }
    }

    async getConsumableCalculators(productId: any, quantity: any) {
        try {
            const model = { product_id: productId, quantity };
            const dataList = await firstValueFrom(this.productService.getConsumableCalculators(model));

            if (dataList) {
                const product = this.modelOrder.quote_product.find((x: any) => x.product.id === productId);
                if (product) {
                    this.reAssignConsumableCalculators(dataList, product);
                }
            }
        } catch (error) {
            console.error(`Failed to fetch consumables for productId ${productId}`, error);
        }
    }

    reAssignConsumableCalculators(newCalculators: any, product: any) {
        let oldCalculators = product.quoteProductCalculators;
        if (oldCalculators?.length > 0 && newCalculators?.length > 0) {
            oldCalculators?.map((consumableCalculator: any, i: any) => {
                newCalculators[i].is_selected = consumableCalculator.is_selected;
                newCalculators[i].id = consumableCalculator.id;
            });
        }
        product.quoteProductCalculators = newCalculators;
    }

    async getQuoteOverallSealerUnit() {
        const requestParam = new RequestParamModel();
        requestParam.fields = ['id', 'quoteOverallSealerUnit'];
        requestParam.expands = ['quoteOverallSealerUnit'];

        try {
            const data = await firstValueFrom(
                this.quoteService.getQuoteDetail(this.modelOrder.id, requestParam)
            );

            if (data) {
                this.modelOrder.quoteOverallSealerUnit = data.quoteOverallSealerUnit;
                this.modelOrder.total_premium_sealing = data.quoteOverallSealerUnit.totalDefenderPrice + data.quoteOverallSealerUnit.totalGoldPrice;
            }
        } catch (error) {
            console.error('Failed to get quote overall sealer unit', error);
        }
    }

    updateQuoteOrder(isCreditReset = false, isReAppliedConsumable = false) {
        return new Promise((resolve, reject) => {
            let json: any = {
                id: this.modelOrder.id,
                shipping_rate: this.modelOrder.shipping_rate,
                deposit_amount: this.modelOrder.deposit_amount,
                final_due_amount: this.modelOrder.final_due_amount,
            }

            if (isReAppliedConsumable) {
                json.is_re_applied_consumable = true;
            }

            this.orderService.updateQuoteOrder(json).subscribe((data) => {
                if (data) {
                    resolve(true);
                    this.changeDetection.detectChanges();
                    this.reCalculateQuotePrice();
                } else {
                    resolve(true);
                }
            }, (error: any) => {
                this.helperFunction.handleError(error);
                reject(false);
            })
        });
    }

    // reCalculateQuotePrice() {
    //   return new Promise((resolve, reject) => {
    //     this.quoteService.updateQuoteSummary(null, this.modelOrder.quote_summary.id).subscribe(() => {
    //       resolve(true);
    //     }, () => {
    //       reject(false);
    //     });
    //   });
    // }

    reCalculateQuotePrice() {
        const payload = {
            quote_id: this.modelOrder.id
        };

        return new Promise((resolve, reject) => {
            this.quoteService.updateQuoteSummary(payload, this.modelOrder.quote_summary.id).subscribe(
                () => resolve(true),
                () => reject(false)
            );
        });
    }

    /**
     * Fetch fresh quote_summary from backend after calculator updates
     * Backend already recalculated totals in updateQuoteProductAppliedCalculator()
     * This method fetches those fresh values and updates the local model
     */
    async refreshQuoteSummary(): Promise<void> {
        try {
            const requestParamModel = new RequestParamModel();
            requestParamModel.expands = ['quoteSummary'];

            const response: any = await firstValueFrom(
                this.quoteService.getQuoteDetail(this.modelOrder.id, requestParamModel, true)
            );

            if (response && response.quote_summary) {
                // Update local quote_summary with fresh values from backend
                this.modelOrder.quote_summary = response.quote_summary;
            }
        } catch (error) {
            console.error('❌ [OrderSummary] Failed to refresh quote summary:', error);
            throw error;
        }
    }

    get showAdhesiveCheckbox(): boolean {
        return this.isCheckboxEnabled();
    }

    get showDisabledAdhesiveCheckbox(): boolean {
        return this.isCheckboxEnabled();
    }

    get showSealingCheckbox(): boolean {
        return this.isCheckboxEnabled();
    }

    get showDisabledSealingCheckbox(): boolean {
        return !this.isCheckboxEnabled();
    }

    private isCheckboxEnabled(): boolean {
        const status = this.modelOrder?.status?.short_name;
        const isArchived = this.modelOrder?.is_archive === this.constants.IS_ARCHIVE_YES;

        const normalCase =
            !this.isQuoteAccepted &&
            !this.isDisabledQuoteDetail &&
            !isArchived &&
            status !== this.constants.QUOTE_EXPIRED_SHORT &&
            !this.isQuoteCancel;

        const depositCase =
            (status === this.constants.QUOTE_DEPOSIT_PAYMENT_DUE_SHORT ||
                status === this.constants.QUOTE_DEPOSIT_PAYMENT_PROCESSING_SHORT) &&
            !isArchived &&
            status !== this.constants.QUOTE_EXPIRED_SHORT &&
            !this.isDisabledQuoteDetail &&
            !this.isQuoteCancel;

        return normalCase || depositCase;
    }

    rangeChanged(event: any) {
        this.sliderValue = event;
    }

    exportInvoice(modelOrder: any) {
        let modalRef = this.modalService1.open(QuoteInvoiceExportModalComponent, {
            centered: false,
            windowClass: 'donationModals quotes-model quote-order-export-pdf-model',
        });
        modalRef.componentInstance.quoteModel = modelOrder;
        modalRef.componentInstance.typeForDownload = Constants.DOWNLOAD_INVOICE;
        modalRef.closed.subscribe((result: any) => {
            if (result) {
                this.getDetailEvent.emit(true);
            }
        });
    }

    onPaymentChanged(event: { depositAmount: number; finalDueAmount: number }) {
        this.modelOrder.quote_summary.deposit_amount = event.depositAmount;
        this.modelOrder.quote_summary.final_due_amount = event.finalDueAmount;
    }

    isSliderUpdateLoadingEvent(event: any) {
        this.isLoadingEvent.emit(event);
    }

    protected readonly Number = Number;

    /**
     * Signed rounded-off amount (system final_total − client net paid) when the
     * absolute difference is within the $0.03 inclusive rounding tolerance.
     *
     * Returns:
     *   - null when there is no rounding to display (diff is 0, or > $0.03,
     *     or the order data needed to compute the diff is unavailable).
     *   - A positive number when the client underpaid by ≤ $0.03 (system absorbs the shortfall).
     *   - A negative number when the client overpaid by ≤ $0.03 (system absorbs the overage).
     */
    get roundedOffAmount(): number | null {
        if (this.modelOrder?.type !== this.constants.ORDER_TYPE_ORDER_SHORT) {
            return null;
        }
        // Compare in NET units. summary.total is net; quote_payment.amount is
        // net (card_charge is surcharge added on top). Using final_total (gross)
        // here would mix units and inflate the residual by the surcharge.
        const total = parseFloat(this.modelOrder?.quote_summary?.total ?? '0');
        if (!total) {
            return null;
        }
        const payments = this.modelOrder?.quote_payment ?? [];
        const netPaid = payments.reduce((sum: number, p: any) => {
            return sum + (parseFloat(p?.amount) || 0);
        }, 0);
        if (netPaid <= 0) {
            return null;
        }
        // Sign convention: positive when overpaid (paid > calculated), negative
        // when underpaid. Round to 2dp to avoid FP precision at the tolerance boundary.
        const diff = Number((netPaid - total).toFixed(2));
        if (diff === 0 || Math.abs(diff) > this.constants.PAYMENT_ROUNDING_TOLERANCE) {
            return null;
        }
        return diff;
    }
}

